import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import type { TaskStatus } from "@/lib/project-types";
import { getTaskStatusTransition, TASK_STATUS_LABEL_KEYS } from "@/lib/task-status";

interface TaskStatusAdvanceButtonProps {
  status: TaskStatus;
  onStatusChange: (status: TaskStatus) => Promise<boolean> | boolean;
  density: "full" | "compact" | "icon";
}

/** The suggested next task action in a full-width conversation header. */
export function TaskStatusAdvanceButton({
  status,
  onStatusChange,
  density,
}: TaskStatusAdvanceButtonProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  if (density !== "full") return null;

  const { nextStatus, labelKey, icon: Icon } = getTaskStatusTransition(status);
  const description = t("projects:taskStatusAdvanceTo", {
    status: t(TASK_STATUS_LABEL_KEYS[nextStatus]),
  });

  const handleClick = async () => {
    if (pending) return;
    setPending(true);
    try {
      await onStatusChange(nextStatus);
    } catch {
      // The caller owns status and error reporting; a failed action leaves it unchanged.
    } finally {
      setPending(false);
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-8 shrink-0 gap-1.5 px-2.5"
      aria-label={description}
      title={description}
      data-testid="task-status-advance-button"
      disabled={pending}
      onClick={() => void handleClick()}
    >
      {pending ? <Spinner className="size-3.5" aria-hidden="true" /> : <Icon className="size-3.5" aria-hidden="true" />}
      <span>{t(labelKey)}</span>
    </Button>
  );
}
